/**
 * The END CUSTOMER's brand in the console header, beside the product's own mark — CRATUS 2.0 #2:
 * "Cratus branding throughout, plus placeholders for end-customer branding".
 *
 * WHOSE: the organisation the console is looking at (the header's selection), else the nearest
 * organisation above it that set one — resolved by the API (`lib/api/brand.ts`). An operator's
 * people see their operator's logo; a Cratus administrator who selects that operator sees what its
 * people see. Nothing set anywhere above: nothing drawn, and the product's mark stands alone.
 *
 * ON A LIGHT PLATE, in both themes. A customer's logo is artwork made for its own letterhead —
 * usually dark ink on white — and on this console's dark header it would simply vanish. The plate
 * is the logo's ground, not a surface of the console, the way the cabinet drawing is beige in both
 * themes (`CabinetDiagram.tsx`).
 *
 * FIRST PAINT: the copy this browser kept (`brand-cache.ts`) is drawn on the first frame and the
 * query confirms it, so a reload never pops the customer in a beat after the rest of the header.
 */
import { useEffect, type ReactNode } from 'react';
import { useOrgContext } from '@/app/org-context-value';
import { keepBrand, readKeptBrand } from '@/lib/api/brand-cache';
import { useBrand, useBrandLogoUrl } from '@/lib/api/brand';

export function CustomerBrand(): ReactNode {
  const { selectedId } = useOrgContext();
  const orgId = selectedId === '' ? null : selectedId;
  const brand = useBrand(orgId);
  const view = brand.data?.effective;
  const logo = useBrandLogoUrl(view, orgId ?? undefined);
  const kept = orgId ? readKeptBrand(orgId) : null;

  /* A brand with no logo is kept here; one with a logo is kept by `useBrandLogoUrl` once fetched. */
  useEffect(() => {
    if (orgId === null || view === undefined) return;
    if (view.logo === null) keepBrand(orgId, { updatedAt: view.updatedAt, name: view.name, dataUrl: null });
  }, [orgId, view]);

  const name = view !== undefined ? view.name : (kept?.name ?? null);
  if (name === null && logo === null) return null;

  return (
    <div className="flex min-w-0 items-center gap-[10px]" aria-label={name ? `${name} — customer` : 'Customer'}>
      <span aria-hidden className="h-[22px] w-px shrink-0" style={{ background: 'var(--border)' }} />
      {logo !== null && (
        <span
          className="grid h-[28px] shrink-0 place-items-center rounded-[6px] px-[6px]"
          style={{ background: '#ffffff', border: '1px solid var(--border)' }}
        >
          <img src={logo} alt={name ?? 'Customer logo'} className="block h-[20px] w-auto max-w-[110px] object-contain" />
        </span>
      )}
      {name !== null && (
        <span className="hidden max-w-[180px] truncate text-[13.5px] font-semibold sm:inline" title={name}>
          {name}
        </span>
      )}
    </div>
  );
}
